// A.5.5 L/M — helpers purs (testables sans DOM) pour les pages Flight Sessions.

export const BADGE = {
  UPLOADED: { c: "#22C55E", l: "UPLOADED" },
  UPLOADING: { c: "#F59E0B", l: "UPLOADING" },
  VERIFIED: { c: "#22C55E", l: "VERIFIED" },
  PENDING: { c: "#737373", l: "PENDING" },
  REMOTE_INTEGRITY_ERROR: { c: "#EF4444", l: "INTEGRITY ERR" },
  READY: { c: "#22C55E", l: "READY" },
  PROCESSING: { c: "#3B82F6", l: "PROCESSING" },
  FAILED: { c: "#EF4444", l: "FAILED" },
  NONE: { c: "#525252", l: "—" },
  NOT_APPLICABLE: { c: "#525252", l: "N/A" },
  READY_FOR_UPLOAD: { c: "#22C55E", l: "COMPLETE" },
  READY_PARTIAL: { c: "#F59E0B", l: "PARTIAL" },
  NOT_EXPECTED: { c: "#737373", l: "NOT EXPECTED" },
  INTEGRITY_ERROR: { c: "#EF4444", l: "INTEGRITY ERR" },
};

export const SEV_COLOR = {
  emergency: "#EF4444", alert: "#EF4444", critical: "#EF4444", error: "#F97316",
  warning: "#F59E0B", notice: "#3B82F6", info: "#737373", debug: "#525252",
};

// COCKPIT.FINAL.9 — projection Flight Session READ-ONLY (PURE). Corrélation SID STRICTE : recorder /
// uploader utilisables UNIQUEMENT si leur session_id === SID du lease ; archive utilisable UNIQUEMENT
// depuis le GET exact (droneId, SID). Aucun fallback latest/approx/mix. ULog/Bundle dispo UNIQUEMENT via
// downloads.ulog/bundle === true. archive 404 => EN ATTENTE (jamais erreur). Aucune state machine.
export function deriveFlightSessionView({ sessionId, liveFlightSession, backendSession } = {}) {
  const sid = sessionId || null;
  const fs = liveFlightSession && typeof liveFlightSession === "object" ? liveFlightSession : {};
  const rec = fs.recorder && typeof fs.recorder === "object" ? fs.recorder : {};
  const up = fs.uploader && typeof fs.uploader === "object" ? fs.uploader : {};

  const recCorrelated = !!sid && rec.session_id === sid;
  const upCorrelated = !!sid && up.session_id === sid;
  // mismatch = un bloc live existe mais pour un AUTRE SID -> non autoritaire.
  const recMismatch = !!sid && rec.session_id != null && rec.session_id !== sid;
  const upMismatch = !!sid && up.session_id != null && up.session_id !== sid;

  const bState = backendSession?.state || "idle"; // idle | waiting_archive | available | error
  const resp = backendSession?.data || null;
  const doc = resp?.session || null;
  const downloads = resp?.downloads || null;

  const droppedEvents = recCorrelated ? (rec.dropped_events ?? 0) : null;
  const droppedCommands = recCorrelated ? (rec.dropped_commands ?? 0) : null;
  const drops = (droppedEvents || 0) > 0 || (droppedCommands || 0) > 0;

  return {
    sessionId: sid,
    hasLease: !!sid,
    live: recCorrelated || upCorrelated,
    recorder: {
      correlated: recCorrelated,
      mismatch: recMismatch,
      status: recCorrelated ? (rec.status ?? null) : null,
      eventsWritten: recCorrelated ? (rec.events_written ?? 0) : null,
      commandsWritten: recCorrelated ? (rec.commands_written ?? 0) : null,
      droppedEvents,
      droppedCommands,
      drops,
    },
    uploader: {
      correlated: upCorrelated,
      mismatch: upMismatch,
      state: upCorrelated ? (up.state ?? null) : null,
      uploadedBytes: upCorrelated ? (up.uploaded_bytes ?? null) : null,
      totalBytes: upCorrelated ? (up.total_bytes ?? null) : null,
    },
    archiveState: bState,                    // waiting_archive = EN ATTENTE (pas une erreur)
    archiveAvailable: bState === "available",
    archiveError: bState === "error",
    archive: doc ? {
      bundleStatus: doc.bundle_status ?? null,
      uploadStatus: doc.upload_status ?? null,
      integrityStatus: doc.integrity_status ?? null,
      durationS: doc.duration_s ?? null,
      totalBytes: doc.total_bytes ?? null,
      fileCount: doc.file_count ?? null,
      createdAt: doc.created_at ?? null,
      uploadedAt: doc.uploaded_at ?? null,
    } : null,
    ulogAvailable: downloads?.ulog === true,   // UNIQUEMENT depuis downloads.ulog
    bundleAvailable: downloads?.bundle === true, // UNIQUEMENT depuis downloads.bundle
    analysisStatus: resp?.analysis_status ?? null,
    analysisError: resp?.analysis_error ?? null,
    derived: resp?.derived ?? null,
  };
}

export function badgeFor(value) {
  return BADGE[value] || { c: "#A3A3A3", l: value || "—" };
}

export function fmtBytes(n) {
  if (n === null || n === undefined) return "—";
  if (n === 0) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i ? 1 : 0)} ${u[i]}`;
}

export function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return s; }
}

// Construit UNIQUEMENT les params non vides (jamais de clé vide envoyée au backend).
export function buildListParams(filters) {
  const params = {};
  if (filters.drone_id) params.drone_id = filters.drone_id;
  if (filters.upload_status) params.upload_status = filters.upload_status;
  if (filters.analysis_status) params.analysis_status = filters.analysis_status;
  return params;
}

export const CHART_METRICS = [
  { metric: "altitude_m", label: "Altitude (m)", color: "#3B82F6" },
  { metric: "ground_speed_mps", label: "Vitesse sol (m/s)", color: "#A78BFA" },
  { metric: "battery_remaining", label: "Batterie (%)", color: "#22C55E" },
  { metric: "gps_satellites", label: "Satellites GPS", color: "#F59E0B" },
  { metric: "gps_hdop", label: "HDOP", color: "#EF4444" },
  { metric: "local_z", label: "Z local (m)", color: "#60A5FA" },
];

// ULOG.OBS.3/4 — intégrité ULog (PUR). Champ absent -> "inconnu", jamais de valeur fabriquée.
export const ULOG_SEV = {
  CRITICAL: { c: "#EF4444", l: "CRITIQUE" },
  WARNING: { c: "#F59E0B", l: "ATTENTION" },
  INFO: { c: "#3B82F6", l: "INFO" },
};

export function ulogIntegrityBadge(level, alert) {
  if (alert === "CRITICAL") return { c: "#EF4444", l: "CRC ✗" };
  if (alert === "WARNING") return { c: "#F59E0B", l: "NON VÉRIFIÉ" };
  if (level === "remote_crc") return { c: "#22C55E", l: "CRC PX4 ✓" };
  if (level === "local_only") return { c: "#3B82F6", l: "LOCAL" };
  return { c: "#525252", l: "—" };
}

export function ulogIntegrityRows(ui) {
  if (!ui) return [];
  const v = (x) => (x === null || x === undefined || x === "" ? "inconnu" : String(x));
  return [
    ["Statut ULog", v(ui.status), "fsd-crc-status"],
    ["Niveau de vérification", v(ui.verification_level), "fsd-crc-level"],
    ["CRC local", v(ui.local_crc32), "fsd-crc-local"],
    ["CRC PX4", v(ui.remote_crc32), "fsd-crc-remote"],
    ["Algorithme", v(ui.crc_algorithm), "fsd-crc-algo"],
    ["CRC distant", v(ui.remote_crc_status), "fsd-crc-remote-status"],
    ["Concordance", ui.crc_match === true ? "oui" : ui.crc_match === false ? "NON" : "inconnu", "fsd-crc-match"],
    ["Erreur", ui.integrity_error ? String(ui.integrity_error) : "aucune", "fsd-crc-error"],
    ["Vérifié le", v(ui.verified_at), "fsd-crc-verified-at"],
  ];
}
